// React
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

// Helpers
import { transformRoute } from "helpers";

// Servisni sloj
import {
  getPlatformActivityAPI,
  getPlatformTenantsAPI,
  getPlatformTrendsAPI,
} from "services/api/platform/api";
import { initialPlatformTrends } from "services/api/platform/consts";
import { computeKpis } from "services/api/platform/transformations";
import { ActivityItem, PlatformTenant, PlatformTrends } from "services/api/platform/types";

// UI komponente
import { Loader } from "ui/components/Loader";
import { PageHeader } from "ui/components/PageHeader";
import { PageTitle } from "ui/components/PageTitle";
import { SETTING_APPLICATION_DASHBOARD_PAGE_TITLE } from "ui/components/PageTitle/consts";

// Moduli specifični za projekt
import { Routes } from "ui/modules/navigation";

// Lokalne komponente
import ActivityFeed from "./components/ActivityFeed/ActivityFeed";
import GrowthSection from "./components/GrowthSection";
import TenantSelector from "./components/TenantSelector";
import KpiCards from "./components/KpiCards/KpiCards";
import TenantTable from "./components/TenantTable/TenantTable";

// Tipovi
import { DashboardPageCmp, DashboardPageProps } from "./types";

/**
 * Nadzorna ploča platforme.
 *
 * Sve brojke dolaze iz baze. Ranija verzija je radila na izmišljenim podacima i imala vlastiti
 * cjenik u pregledniku; cijene sada dolaze iz cjenika kroz resolver.
 */
const DashboardPage: DashboardPageCmp = (_props: DashboardPageProps) => {
  const title = SETTING_APPLICATION_DASHBOARD_PAGE_TITLE;
  const navigate = useNavigate();

  const [tenants, setTenants] = useState<PlatformTenant[]>([]);
  const [activity, setActivity] = useState<ActivityItem[]>([]);
  const [trends, setTrends] = useState<PlatformTrends>(initialPlatformTrends);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  /** Prazno znači pregled cijele platforme. */
  const [selectedTenantId, setSelectedTenantId] = useState<string | null>(null);

  useEffect(() => {
    fetchInitial();
  }, []);

  const fetchInitial = async () => {
    setIsLoading(true);

    try {
      const [all, recent, monthly] = await Promise.all([
        getPlatformTenantsAPI(),
        getPlatformActivityAPI(25),
        getPlatformTrendsAPI(12),
      ]);

      setTenants(all);
      setActivity(recent);
      setTrends(monthly);
    } catch {
      // greška se prikazuje kroz snackbar
    } finally {
      setIsLoading(false);
    }
  };

  /* Odabir klijenta sužava sve brojke na njega; bez odabira vrijede za cijelu platformu. */
  const scopedTenants = useMemo(
    () =>
      selectedTenantId
        ? tenants.filter((tenant) => tenant.tenantId === selectedTenantId)
        : tenants,
    [tenants, selectedTenantId]
  );

  const kpis = useMemo(() => computeKpis(scopedTenants), [scopedTenants]);

  const handleOpenSelectedDetail = useCallback(() => {
    if (selectedTenantId) {
      navigate(transformRoute(Routes.TenantDetailPageRouter, { tenantId: selectedTenantId }));
    }
  }, [selectedTenantId, navigate]);

  const tenantNames = useMemo(
    () => new Map(tenants.map((tenant) => [tenant.tenantId, tenant.name])),
    [tenants]
  );

  const handleOpenTenant = useCallback(
    (tenantId: string) => {
      navigate(transformRoute(Routes.TenantDetailPageRouter, { tenantId }));
    },
    [navigate]
  );

  /*
    Dok se učitava ne prikazuje se ništa osim samog učitavanja.

    Ranije su zaglavlje i izbornik klijenta stajali iznad praznine: izbornik je bio prazan i
    klikabilan, a naslov je tvrdio „Zbrojeno kroz sve klijente" nad ničim.
  */
  if (isLoading) {
    return (
      <Loader isLoading className="fixed inset-0" message="Učitavanje podataka…">
        {null}
      </Loader>
    );
  }

  return (
    <div>
      <div className="bg-white rounded-t-md border-b border-slate-200">
        <PageHeader>
          <PageTitle title={title.text} icon={title.icon} iconColor={title.iconColor} />
        </PageHeader>
      </div>
      <div className="px-4 py-6 space-y-4">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 className="text-lg font-bold text-slate-900">
              {selectedTenantId ? "Pregled klijenta" : "Pregled platforme"}
            </h2>
            <p className="text-sm text-slate-500">
              {selectedTenantId
                ? "Metrike odabranog klijenta"
                : "Zbrojeno kroz sve klijente"}
            </p>
          </div>

          <TenantSelector
            value={selectedTenantId}
            tenants={tenants}
            onSelect={setSelectedTenantId}
            onOpenDetail={handleOpenSelectedDetail}
          />
        </div>

        <KpiCards kpis={kpis} />

        {/* Grafovi se ne filtriraju po klijentu — rast i raspodjela su svojstvo platforme. */}
        {!selectedTenantId && <GrowthSection trends={trends} />}

        {/*
          Klijenti zauzimaju dvije trećine, aktivnost trećinu. Tablica ima sedam stupaca i
          treba širinu; zapis aktivnosti je uzak popis i u punoj širini je bio uglavnom
          prazan prostor.

          Ispod XL se slaže jedno pod drugo — na užem zaslonu bi trećina bila neupotrebljiva.
        */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 items-start">
          <div className="xl:col-span-2 min-w-0">
            <TenantTable tenants={scopedTenants} onOpenTenant={handleOpenTenant} />
          </div>

          <ActivityFeed items={activity} tenantNames={tenantNames} />
        </div>
      </div>
    </div>
  );
};

DashboardPage.route = Routes.DashboardPageRouter;

export default DashboardPage;
